"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { NotebookPen } from "lucide-react";
import DashboardCard from "./DashboardCard";
import DashboardCardLoading from "./DashboardCardLoading";
import { SUBJECT_LABELS, type NoteListItem } from "@/types/note";

/** ISO 문자열을 "6월 5일" 형태로. */
function formatLastSaved(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("ko-KR", { month: "long", day: "numeric" });
}

/**
 * 대시보드 "최근 필기" 위젯 (JCG-2073).
 * `/api/notes/list`(lastSaved desc 정렬)에서 가장 최근 필기 1개를 보여주고, 클릭 시 해당 글로 이동.
 */
export default function DashboardRecentNote() {
  const [note, setNote] = useState<NoteListItem | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch("/api/notes/list", { cache: "no-store" });
        if (res.ok) {
          const data = await res.json();
          if (alive) setNote((data.notes ?? [])[0] ?? null);
        }
      } catch (e) {
        console.error("대시보드 필기 로드 오류:", e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  return (
    <DashboardCard icon={NotebookPen} title="최근 필기" href="/my/notes">
      {loading ? (
        <DashboardCardLoading height={70} label="필기 불러오는 중…" />
      ) : note ? (
        <Link
          href={note.pagePath}
          className="block rounded-lg border border-border bg-background px-3 py-2 transition-colors hover:border-amber-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 dark:hover:border-amber-700"
        >
          <span className="block truncate text-[13px] font-semibold text-foreground">
            {note.pageTitle}
          </span>
          <span className="mt-0.5 block truncate text-[11px] text-muted-foreground">
            {SUBJECT_LABELS[note.subject] ?? note.subject} · {formatLastSaved(note.lastSaved)}
          </span>
        </Link>
      ) : (
        <p className="py-6 text-center text-[13px] text-muted-foreground">
          아직 작성한 필기가 없어요.
        </p>
      )}
    </DashboardCard>
  );
}
